import {ApiUtil} from './ApiUtil'
import {
  mapClaimsResponse,
  mapDrawingResponse,
  mapGenerateProductListResponse,
  mapServicePricesResponse
} from './response-mappers'
import type {ClaimsResponse, FailedResponse, GenerateProductListResponse, ServicePricesResponse} from './types'
import type {
  BuildingType,
  PlanState,
  SmokeDetectorType,
  SystemType,
  WaterMeterType,
  WirelessMode,
  YesNo
} from '../reducers/types'

/* Response types */

interface SuccessfulClaimsQueryResponse {
  succeeded: true
  payload: ClaimsResponse
}

export type ClaimsQueryResponse = SuccessfulClaimsQueryResponse | FailedResponse

export interface CreateReportRequestPayload {
  contact: boolean
  email: string
  plan: PlanState
  message: string
}

interface SuccessfulProductListResponse {
  succeeded: true
  payload: GenerateProductListResponse
}

export type ProductListResponse = SuccessfulProductListResponse | FailedResponse

interface SuccessfulReportCreateResponse {
  succeeded: true
}

export type ReportCreateResponse = SuccessfulReportCreateResponse | FailedResponse

interface SuccessfulServicePricesResponse {
  succeeded: true,
  payload: ServicePricesResponse
}

export type GetServicePricesResponse = SuccessfulServicePricesResponse | FailedResponse

interface SuccessfulSvgDrawingResponse {
  succeeded: true
  payload: {
    svg: string
  }
}

export type SvgDrawingResponse = SuccessfulSvgDrawingResponse | FailedResponse

export interface SvgDrawingGenerationRequest {
  buildingType: BuildingType
  systemType: SystemType
  wirelessMode?: WirelessMode
  waterMeterType: WaterMeterType
  temperatureMetersEnabled?: YesNo
  apartmentScreensEnabled?: YesNo
  moreThanOneMeterPerApartment?: YesNo
  smokeDetectorType?: Exclude<SmokeDetectorType, 'none'>
  floorQuantity: number
  apartmentQuantity: number
  stairwellQuantity: number
}

/* API methods */

/**
 * Get User's claims (details, permissions).
 * @param token authentication token.
 */
async function getClaims(token: string): Promise<ClaimsQueryResponse> {
  const apiDesc = 'claims data query'
  const url = ApiUtil.getApiUrl('/auth/claims')
  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapClaimsResponse(payload)

    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Generate product list based on design plan.
 * @param input request input.
 * @param token authentication token.
 */
async function generateProductList(input: PlanState, token: string): Promise<ProductListResponse> {
  const apiDesc = 'generate product list request'
  const url = ApiUtil.getApiUrl('/planner/product-list')
  const body = {...input}

  try {
    const response = await ApiUtil.doPostRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapGenerateProductListResponse(payload)

    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc, body)
  }
}

/**
 * Get URI to be used as image source for SVG drawing.
 */
async function getServicePrices(token: string): Promise<GetServicePricesResponse> {
  const apiDesc = 'get service prices'

  const url = ApiUtil.getApiUrl('/planner/service-prices')

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapServicePricesResponse(payload)

    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get URI to be used as image source for SVG drawing.
 */
async function getSvgDrawing(input: SvgDrawingGenerationRequest, token: string): Promise<SvgDrawingResponse> {
  const apiDesc = 'generate plan SVG drawing'

  const url = ApiUtil.getApiUrl('/planner/drawing')
  const body = {...input}

  try {
    const response = await ApiUtil.doPostRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapDrawingResponse(payload)
    const {svg: base64EncodedSvg} = parsedPayload

    return {
      succeeded: true,
      payload: {
        svg: base64EncodedSvg
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Send report to the user's email box.
 * @param input request input.
 * @param token authentication token.
 */
async function sendReport(input: CreateReportRequestPayload, token: string): Promise<ReportCreateResponse> {
  const apiDesc = 'send report request'
  const emailParam = encodeURIComponent(input.email)
  const contactParam = input.contact.toString()
  const messageParam = encodeURIComponent(input.message)

  const apiPath = `/planner/send-report?email=${emailParam}&contact=${contactParam}&msg=${messageParam}`
  const url = ApiUtil.getApiUrl(apiPath)
  const body = {...input.plan}
  try {
    const response = await ApiUtil.doPostRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    return {
      succeeded: true
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc, body)
  }
}

/**
 * Backend API for non-admin section.
 */
export const BackendAPI = {
  generateProductList,
  getClaims,
  getServicePrices,
  getSvgDrawing,
  sendReport
}

export type BackendApiType = typeof BackendAPI
